import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, asArray, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import AssigneeSelect from "@/components/AssigneeSelect";
import CustomFieldInputs from "@/components/CustomFieldInputs";
import { useAuth } from "@/context/AuthContext";

const PRIORITIES = ["critical", "high", "medium", "low"];

// Mirrors backend/authz.py's CLIENT_SCOPED_ROLES. A client-scoped caller IS
// the requester -- the server forces requester_kind/requester_id to
// themselves regardless of what a picker would send (backend/server.py's
// _apply_requester) -- so the picker below is never offered to one; showing
// it would be a control that looks live and is silently overridden.
const CLIENT_SCOPED_ROLES = [
  "client_read_only", "client_admin", "client_administrator",
  "client_manager", "client_user", "client_contact",
];

/** Raise a ticket, blank or from a template. Portals via the shared Radix
 *  Dialog (document.body) rather than a hand-rolled `fixed inset-0` div --
 *  <main> carries a `.fade-up` entrance animation whose `to` keyframe leaves
 *  a non-"none" `transform` in place (animation-fill-mode: both), which turns
 *  <main> into the containing block for any `position: fixed` DESCENDANT of
 *  it. A hand-rolled fixed div lands hundreds of pixels below the fold; the
 *  portalled Dialog escapes that containing block entirely. */
export default function NewTicketDialog({ onClose, onCreated, presetClientId, presetAssetId }) {
  const nav = useNavigate();
  const { user } = useAuth();
  const isClientScoped = !!user && CLIENT_SCOPED_ROLES.includes(user.role);
  const [mode, setMode] = useState("blank");
  const [clients, setClients] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [preview, setPreview] = useState(null);
  const [f, setF] = useState({
    title: "", description: "", client_id: presetClientId || "",
    category: "general", priority: "medium",
    asset_id: presetAssetId || "", assignee_id: "", template_id: "", requester_id: "",
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  // Sub-project I, Task 9: custom fields on the create form. `committedCategory`
  // is the category `customDefs` was actually fetched for -- separate from
  // `f.category`, which changes on every keystroke, so a fetch does not fire
  // (and no field disappears mid-typing) until the operator commits to a
  // category by leaving the field. `custom` is the values buffer, keyed the
  // same way TicketDetail.jsx's own edit buffer is.
  const [committedCategory, setCommittedCategory] = useState(f.category);
  const [customDefs, setCustomDefs] = useState([]);
  const [custom, setCustom] = useState({});

  useEffect(() => {
    if (!committedCategory) { setCustomDefs([]); return; }
    api.get("/ticket-field-defs/for-category", { params: { category: committedCategory } })
      .then((r) => setCustomDefs(asArray(r.data)))
      .catch(() => setCustomDefs([]));
  }, [committedCategory]);

  // ⚠️ The create form re-renders its custom fields when the category
  // changes -- a value typed into a field that then disappears is
  // discarded, and this must say so BEFORE it happens, not after. Fired on
  // BLUR (once the operator has actually finished typing a category),
  // never on every keystroke -- a confirm on every character would make
  // the field unusable. A cancelled confirm reverts the input back to the
  // last committed category rather than leaving it showing a value nothing
  // was fetched for.
  const commitCategory = () => {
    const next = f.category.trim();
    if (next === committedCategory) return;
    const typedValues = customDefs.filter((d) => {
      const v = custom[d.key];
      return v !== undefined && v !== "" && v !== false;
    });
    if (typedValues.length && !window.confirm(
      `Changing the category to "${next || "(blank)"}" will discard the value(s) already ` +
      `entered for: ${typedValues.map((d) => d.label).join(", ")}. Continue?`)) {
      set("category", committedCategory);
      return;
    }
    if (typedValues.length) setCustom({});
    setCommittedCategory(next);
  };

  useEffect(() => {
    api.get("/clients").then((r) => setClients(asArray(r.data))).catch(() => setClients([]));
    api.get("/desk/templates").then((r) => setTemplates(asArray(r.data))).catch(() => setTemplates([]));
    // /users is the only enumerable list of requesters this backend exposes
    // today (requester_kind "contact" points into ticket_contacts, which has
    // no listing route -- those rows only ever get written by mail intake).
    // A client's portal accounts -- client_admin/client_read_only/etc -- ARE
    // its contacts in the sense this picker needs: real people at that
    // client who can be named as who a ticket is for. Skipped entirely for a
    // client-scoped caller, who is never offered this picker anyway.
    if (!isClientScoped) {
      api.get("/users").then((r) => setContacts(asArray(r.data))).catch(() => setContacts([]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const requesterOptions = contacts.filter(
    (u) => u.client_id === f.client_id && CLIENT_SCOPED_ROLES.includes(u.role));

  // The deadline preview comes from the SERVER, via the same compute_deadlines
  // the insert path calls. Recomputing business hours in JS would be a second
  // implementation of the one calculation that must stay singular.
  useEffect(() => {
    const qs = new URLSearchParams({ priority: f.priority });
    if (f.client_id) qs.set("client_id", f.client_id);
    api.get(`/desk/sla/preview?${qs}`).then((r) => setPreview(r.data)).catch(() => setPreview(null));
  }, [f.priority, f.client_id]);

  const submit = async () => {
    if (mode === "blank" && !f.title.trim()) return toast.error("A title is required.");
    setBusy(true);
    setErr("");
    try {
      let tkt;
      if (mode === "template") {
        if (!f.template_id) { toast.error("Pick a template."); setBusy(false); return; }
        const { data } = await api.post(`/desk/templates/${f.template_id}/raise`,
          { client_id: f.client_id || undefined, asset_id: f.asset_id || undefined });
        tkt = data;
      } else {
        const body = {
          title: f.title.trim(), description: f.description.trim(),
          category: f.category, priority: f.priority,
        };
        if (f.client_id) body.client_id = f.client_id;
        if (f.asset_id) body.asset_id = f.asset_id;
        if (f.assignee_id) body.assignee_id = f.assignee_id;
        // Optional, and never offered to a client-scoped caller -- see
        // CLIENT_SCOPED_ROLES above. requester_kind is always "user" here:
        // the only enumerable identity space this picker can reach.
        if (f.requester_id) { body.requester_kind = "user"; body.requester_id = f.requester_id; }
        // Custom fields (Task 9): only the ones with a real, non-empty
        // value -- an untouched optional field is simply absent from the
        // payload rather than sent as "", which `create_ticket` would hand
        // to `validate_custom`'s type coercion and refuse for every type
        // but text/checkbox.
        const customBody = {};
        for (const d of customDefs) {
          const v = custom[d.key];
          if (v === undefined || v === "") continue;
          customBody[d.key] = v;
        }
        if (Object.keys(customBody).length) body.custom = customBody;
        const { data } = await api.post("/tickets", body);
        tkt = data;
      }
      toast.success(`Ticket #${tkt.number} raised`);
      onCreated?.(tkt);
      nav(`/tickets/${tkt.id}`);
    } catch (e) {
      // formatApiError + a PERSISTENT banner (see the render below), not
      // only a toast -- this app's one global <Toaster> has no
      // richColors, so an error toast is the same neutral colour as a
      // success one, and a validation 422 from the new custom-fields
      // payload is exactly the kind of refusal a technician needs to
      // still see a moment later.
      setErr(formatApiError(e));
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  const fmt = (iso) => iso ? new Date(iso).toLocaleString(undefined,
    { weekday: "short", hour: "2-digit", minute: "2-digit", day: "numeric", month: "short" }) : "—";

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent
        data-testid="new-ticket-dialog"
        className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-2xl max-h-[85vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-display">New ticket</DialogTitle>
          <DialogDescription className="sr-only">
            Raise a ticket from a blank form or from a template, and see the service level it will get before submitting.
          </DialogDescription>
        </DialogHeader>

        {err && (
          <div className="border border-red-900/50 bg-red-950/30 text-red-300 rounded px-4 py-3 text-sm"
            data-testid="new-ticket-error">
            {err}
          </div>
        )}

        <div className="flex gap-1 border-b border-zinc-900">
          {[["blank", "Blank"], ["template", "From template"]].map(([k, l]) => (
            <button key={k} onClick={() => setMode(k)} data-testid={`new-ticket-mode-${k}`}
              className={`px-3 py-2 text-sm border-b-2 -mb-px ${mode === k
                ? "border-blue-500 text-zinc-100" : "border-transparent text-zinc-500 hover:text-zinc-300"}`}>{l}</button>
          ))}
        </div>

        <div className="space-y-3">
          {mode === "template" && (
            <label className="block">
              <span className="text-xs text-zinc-400">Template</span>
              <select value={f.template_id} onChange={(e) => {
                set("template_id", e.target.value);
                const t = templates.find((x) => x.id === e.target.value);
                if (t) {
                  set("title", t.name); set("priority", t.priority || "medium");
                  const cat = t.category || "general";
                  set("category", cat);
                  // Template creation goes through a different route
                  // (POST /desk/templates/{id}/raise) that carries no
                  // custom section on this form -- still resync so the
                  // fetched defs never point at a stale category if the
                  // operator switches back to "Blank".
                  setCommittedCategory(cat); setCustom({});
                }
              }} data-testid="new-ticket-template"
                className="w-full mt-1 bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm">
                <option value="">Choose…</option>
                {templates.map((t) => <option key={t.id} value={t.id}>{t.name} ({t.tasks?.length || 0} steps)</option>)}
              </select>
            </label>
          )}

          {mode === "blank" && (
            <>
              <label className="block">
                <span className="text-xs text-zinc-400">Title</span>
                <input value={f.title} onChange={(e) => set("title", e.target.value)}
                  data-testid="new-ticket-title" autoFocus
                  className="w-full mt-1 bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500/60" />
              </label>
              <label className="block">
                <span className="text-xs text-zinc-400">Description</span>
                <textarea value={f.description} onChange={(e) => set("description", e.target.value)}
                  rows={4} data-testid="new-ticket-description"
                  className="w-full mt-1 bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500/60" />
              </label>
            </>
          )}

          <div className="grid grid-cols-2 gap-3">
            <label className="block min-w-0">
              <span className="text-xs text-zinc-400">Client</span>
              <select value={f.client_id}
                onChange={(e) => { set("client_id", e.target.value); set("requester_id", ""); }}
                data-testid="new-ticket-client"
                className="w-full mt-1 bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm">
                <option value="">— none —</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </label>
            {!isClientScoped && (
              <label className="block min-w-0">
                <span className="text-xs text-zinc-400">Requester (optional)</span>
                <select value={f.requester_id} onChange={(e) => set("requester_id", e.target.value)}
                  disabled={!f.client_id} data-testid="new-ticket-requester"
                  className="w-full mt-1 bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm disabled:opacity-50">
                  <option value="">
                    {f.client_id ? "— none —" : "Choose a client first"}
                  </option>
                  {requesterOptions.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              </label>
            )}
            <label className="block min-w-0">
              <span className="text-xs text-zinc-400">Priority</span>
              <select value={f.priority} onChange={(e) => set("priority", e.target.value)}
                data-testid="new-ticket-priority"
                className="w-full mt-1 bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm">
                {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </label>
            <label className="block min-w-0">
              <span className="text-xs text-zinc-400">Category</span>
              <input value={f.category} onChange={(e) => set("category", e.target.value)}
                onBlur={commitCategory}
                data-testid="new-ticket-category"
                className="w-full mt-1 bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm" />
            </label>
            <label className="block min-w-0">
              <span className="text-xs text-zinc-400">Assignee</span>
              <div className="mt-1 flex">
                <AssigneeSelect value={f.assignee_id} onChange={(v) => set("assignee_id", v)} testid="new-ticket-assignee" />
              </div>
            </label>
          </div>

          <div className="bg-zinc-900/50 border border-zinc-900 rounded p-3" data-testid="new-ticket-sla-preview">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-1">
              Service level this ticket will get
            </div>
            {preview ? (
              <div className="text-sm text-zinc-300 leading-relaxed">
                Respond by <span className="font-mono text-zinc-100">{fmt(preview.response_due_at)}</span>,
                resolve by <span className="font-mono text-zinc-100">{fmt(preview.sla_deadline)}</span>.
                <div className="text-xs text-zinc-500 mt-1">
                  {preview.sla_policy} ({preview.sla_source}) · {preview.sla_calendar}
                </div>
              </div>
            ) : <div className="text-sm text-zinc-500">Could not compute a preview.</div>}
          </div>

          {mode === "blank" && customDefs.length > 0 && (
            <div className="bg-zinc-900/50 border border-zinc-900 rounded p-3" data-testid="new-ticket-custom-fields">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-2">
                Custom fields for "{committedCategory || "general"}"
              </div>
              <CustomFieldInputs defs={customDefs} values={custom}
                onChange={(k, v) => setCustom((c) => ({ ...c, [k]: v }))} />
            </div>
          )}
        </div>

        <DialogFooter>
          <button onClick={onClose} className="border border-zinc-800 rounded px-3 py-1.5 text-sm text-zinc-300">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="new-ticket-submit"
            className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded px-4 py-1.5 text-sm">
            {busy ? "Raising…" : "Raise ticket"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
